import type { SQLiteDatabase } from 'expo-sqlite';

import { newId, now } from '@/db/ids';
import type { ExpenseRecurring, ExpenseRow } from '@/db/types';
import { bumpDataRevision } from '@/lib/data-revision';

/**
 * Curated expense categories that match how homeowners actually think about
 * money — utilities, mortgage, insurance, taxes, maintenance, improvements,
 * HOA/condo fees. Order matters; first 6 are surfaced as quick-pick chips.
 */
export const EXPENSE_CATEGORIES = [
  'Mortgage',
  'Utilities',
  'Insurance',
  'Property tax',
  'Maintenance',
  'Improvements',
  'HOA / condo fees',
  'Trash & recycling',
  'Internet & cable',
  'Pest control',
  'Landscaping',
  'Cleaning',
  'Snow removal',
  'Pool service',
  'Other',
] as const;

export const RECURRING_OPTIONS: { v: ExpenseRecurring; label: string; days: number }[] = [
  { v: 'monthly', label: 'Every month', days: 30 },
  { v: 'quarterly', label: 'Every 3 months', days: 90 },
  { v: 'biannual', label: 'Twice a year', days: 180 },
  { v: 'annual', label: 'Yearly', days: 365 },
];

export interface NewExpenseInput {
  propertyId: string;
  systemId?: string | null;
  category: string;
  vendor?: string | null;
  amount_cents: number;
  paid_on: number;
  description?: string | null;
  recurring?: ExpenseRecurring | null;
  tax_deductible?: number;
  receipt_doc_id?: string | null;
  notes?: string | null;
}

export async function addExpense(
  db: SQLiteDatabase,
  input: NewExpenseInput
): Promise<string> {
  const id = newId();
  const t = now();
  await db.runAsync(
    `INSERT INTO expense (
      id, property_id, system_id, category, vendor, amount_cents, paid_on,
      description, recurring, tax_deductible, receipt_doc_id, notes,
      created_at, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    id,
    input.propertyId,
    input.systemId ?? null,
    input.category,
    input.vendor ?? null,
    input.amount_cents,
    input.paid_on,
    input.description ?? null,
    input.recurring ?? null,
    input.tax_deductible ?? 0,
    input.receipt_doc_id ?? null,
    input.notes ?? null,
    t,
    t
  );
  bumpDataRevision();
  return id;
}

export interface UpdateExpenseInput {
  systemId?: string | null;
  category?: string;
  vendor?: string | null;
  amount_cents?: number;
  paid_on?: number;
  description?: string | null;
  recurring?: ExpenseRecurring | null;
  tax_deductible?: number;
  receipt_doc_id?: string | null;
  notes?: string | null;
}

export async function updateExpense(
  db: SQLiteDatabase,
  id: string,
  input: UpdateExpenseInput
): Promise<void> {
  const t = now();
  const sets: string[] = [];
  const args: (string | number | null)[] = [];
  const fields: (keyof UpdateExpenseInput)[] = [
    'systemId', 'category', 'vendor', 'amount_cents', 'paid_on', 'description',
    'recurring', 'tax_deductible', 'receipt_doc_id', 'notes',
  ];
  const COL: Partial<Record<keyof UpdateExpenseInput, string>> = {
    systemId: 'system_id',
  };
  for (const f of fields) {
    if (input[f] !== undefined) {
      sets.push(`${COL[f] ?? f} = ?`);
      args.push((input[f] as string | number | null) ?? null);
    }
  }
  if (sets.length === 0) return;
  sets.push('updated_at = ?');
  args.push(t);
  args.push(id);
  await db.runAsync(`UPDATE expense SET ${sets.join(', ')} WHERE id = ?`, ...args);
  bumpDataRevision();
}

/** Bulk category reassign (Wave 61 mass-edit). One UPDATE per chunk of
 *  ids — SQLite's variable cap is 999, so chunk defensively. */
export async function bulkSetExpenseCategory(
  db: SQLiteDatabase,
  ids: string[],
  category: string
): Promise<void> {
  if (ids.length === 0) return;
  const t = now();
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    await db.runAsync(
      `UPDATE expense SET category = ?, updated_at = ? WHERE id IN (${chunk.map(() => '?').join(',')})`,
      category,
      t,
      ...chunk
    );
  }
  bumpDataRevision();
}

/** Bulk soft-delete (Wave 61 mass-edit). */
export async function bulkDeleteExpenses(
  db: SQLiteDatabase,
  ids: string[]
): Promise<void> {
  if (ids.length === 0) return;
  const t = now();
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    await db.runAsync(
      `UPDATE expense SET deleted_at = ?, updated_at = ? WHERE id IN (${chunk.map(() => '?').join(',')})`,
      t,
      t,
      ...chunk
    );
  }
  bumpDataRevision();
}

/**
 * Wave 61 — user-defined categories. Stored per-property as a JSON
 * array of names (property.custom_expense_categories_json) and merged
 * AFTER the built-ins in every picker, before 'Other'. Names are
 * trimmed and deduped case-insensitively against both lists.
 */
export function parseCustomCategories(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
  } catch {
    return [];
  }
}

/** Built-ins (minus 'Other') + customs + 'Other' last. */
export function allExpenseCategories(customJson: string | null | undefined): string[] {
  const builtins = EXPENSE_CATEGORIES.filter((c) => c !== 'Other');
  const customs = parseCustomCategories(customJson).filter(
    (c) => !builtins.some((b) => b.toLowerCase() === c.toLowerCase())
  );
  return [...builtins, ...customs, 'Other'];
}

/** Persist a new custom category; returns the cleaned name, or null if
 *  it was empty / already exists (either list). */
export async function addCustomExpenseCategory(
  db: SQLiteDatabase,
  propertyId: string,
  customJson: string | null | undefined,
  name: string
): Promise<string | null> {
  const clean = name.trim().slice(0, 40);
  if (!clean) return null;
  const existing = allExpenseCategories(customJson);
  if (existing.some((c) => c.toLowerCase() === clean.toLowerCase())) return clean;
  const customs = [...parseCustomCategories(customJson), clean];
  await db.runAsync(
    `UPDATE property SET custom_expense_categories_json = ?, updated_at = ? WHERE id = ?`,
    JSON.stringify(customs),
    now(),
    propertyId
  );
  bumpDataRevision();
  return clean;
}

export async function deleteExpense(db: SQLiteDatabase, id: string): Promise<void> {
  const t = now();
  await db.runAsync(
    `UPDATE expense SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    id
  );
  bumpDataRevision();
}

export async function getExpenseById(
  db: SQLiteDatabase,
  id: string
): Promise<ExpenseRow | null> {
  return await db.getFirstAsync<ExpenseRow>(
    `SELECT * FROM expense WHERE id = ? AND deleted_at IS NULL`,
    id
  );
}

export async function listExpenses(
  db: SQLiteDatabase,
  propertyId: string
): Promise<ExpenseRow[]> {
  return await db.getAllAsync<ExpenseRow>(
    `SELECT * FROM expense
     WHERE property_id = ? AND deleted_at IS NULL
     ORDER BY paid_on DESC`,
    propertyId
  );
}

export interface ExpenseTotals {
  ytdCents: number;
  last12Cents: number;
  monthlyAvgCents: number;
  byCategory: Record<string, number>;
  recurringMonthlyCents: number;
  /** Total of all expenses ever logged on this property. */
  lifetimeCents: number;
  /** Same-period-last-year YTD total — Jan 1 (prev year) through today (prev year). */
  prevYearSamePeriodCents: number;
  /** Per-year totals, newest first. Useful for sparkline / rollup card. */
  byYear: { year: number; cents: number }[];
}

export async function getExpenseTotals(
  db: SQLiteDatabase,
  propertyId: string
): Promise<ExpenseTotals> {
  const expenses = await listExpenses(db, propertyId);
  const today = new Date();
  const thisYear = today.getFullYear();
  const yearStart = new Date(thisYear, 0, 1).getTime();
  const last12Start = Date.now() - 365 * 24 * 60 * 60 * 1000;
  const prevYearStart = new Date(thisYear - 1, 0, 1).getTime();
  // Same calendar day in the previous year — defines the "same period" window.
  const prevYearSameDay = new Date(
    thisYear - 1,
    today.getMonth(),
    today.getDate(),
    23,
    59,
    59
  ).getTime();

  let ytdCents = 0;
  let last12Cents = 0;
  let lifetimeCents = 0;
  let prevYearSamePeriodCents = 0;
  const byCategory: Record<string, number> = {};
  const byYearMap = new Map<number, number>();
  let recurringMonthlyCents = 0;

  for (const e of expenses) {
    lifetimeCents += e.amount_cents;
    if (e.paid_on >= yearStart) ytdCents += e.amount_cents;
    if (e.paid_on >= last12Start) last12Cents += e.amount_cents;
    if (e.paid_on >= prevYearStart && e.paid_on <= prevYearSameDay) {
      prevYearSamePeriodCents += e.amount_cents;
    }
    byCategory[e.category] = (byCategory[e.category] ?? 0) + e.amount_cents;
    const y = new Date(e.paid_on).getFullYear();
    byYearMap.set(y, (byYearMap.get(y) ?? 0) + e.amount_cents);
    if (e.recurring) {
      const opt = RECURRING_OPTIONS.find((o) => o.v === e.recurring);
      if (opt) {
        // Normalize to monthly: amount × (30 / days-between).
        recurringMonthlyCents += Math.round((e.amount_cents * 30) / opt.days);
      }
    }
  }

  const monthsElapsed = Math.max(
    1,
    Math.round((Date.now() - yearStart) / (30 * 24 * 60 * 60 * 1000))
  );
  const monthlyAvgCents = Math.round(ytdCents / monthsElapsed);

  const byYear = Array.from(byYearMap.entries())
    .map(([year, cents]) => ({ year, cents }))
    .sort((a, b) => b.year - a.year);

  return {
    ytdCents,
    last12Cents,
    monthlyAvgCents,
    byCategory,
    recurringMonthlyCents,
    lifetimeCents,
    prevYearSamePeriodCents,
    byYear,
  };
}
